import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { Plug } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import { IntegrationsPage } from "./pages/IntegrationsPage";

/** Integrations Monitor (COPS-3): event-clock anomaly detection over inbound
 *  feeds. Tabs live in the URL (code_rag pattern); the incident drawer is
 *  driven by ?incident= so a deep link opens straight onto it. */

export type IntegrationsTab =
  | "overview"
  | "integrations"
  | "companies"
  | "locations"
  | "new"
  | "watchlist"
  | "admin"
  | "how";

export interface IntegrationsSearch {
  tab?: IntegrationsTab;
  incident?: string;
  expanded?: boolean;
  /** Ad-hoc drill target (any L1/L2/L3 key) for the entity drawer. */
  entity?: string;
  entityLevel?: string;
}

const TABS: IntegrationsTab[] = [
  "overview", "integrations", "companies", "locations", "new", "watchlist", "admin", "how",
];

export function validateIntegrationsSearch(
  search: Record<string, unknown>,
): IntegrationsSearch {
  const out: IntegrationsSearch = {};
  if (typeof search.tab === "string" && TABS.includes(search.tab as IntegrationsTab)) {
    out.tab = search.tab as IntegrationsTab;
  }
  if (typeof search.incident === "string" && search.incident) out.incident = search.incident;
  if (search.expanded === true || search.expanded === "true") out.expanded = true;
  if (typeof search.entity === "string" && search.entity) out.entity = search.entity;
  if (typeof search.entityLevel === "string" && /^L[123]$/.test(search.entityLevel)) {
    out.entityLevel = search.entityLevel;
  }
  return out;
}

export const integrationsModule: FeatureModule = {
  key: "integrations",
  nav: {
    label: "Integrations",
    icon: Plug,
    to: "/integrations",
    requires: "integrations.read",
    order: 20,
    group: "Monitors",
  },
  routes: (rootRoute) => [
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/integrations",
      component: guarded("integrations.read", IntegrationsPage),
      validateSearch: validateIntegrationsSearch,
    }),
  ] as unknown as AnyRoute[],
};
